import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Loader2, CheckCircle2, XCircle } from "lucide-react";

const POLL_MAX = 10;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/donations/status/${sid}`);
        if (data.payment_status === "paid") { setInfo(data); setState("paid"); refresh?.(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      tries.current += 1;
      if (tries.current >= POLL_MAX) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refresh]);

  return (
    <div className="max-w-md mx-auto px-4 pt-20 pb-20 text-center" data-testid="donate-success-page">
      {state === "checking" && (
        <><Loader2 size={40} className="animate-spin text-[#FF2A5F] mx-auto mb-5" />
        <p className="text-[#9CA3AF]">Confirming your donation…</p></>
      )}
      {state === "paid" && (
        <div data-testid="donate-paid">
          <span className="inline-flex w-16 h-16 rounded-full bg-[#00E676]/[0.15] items-center justify-center mb-5"><CheckCircle2 size={34} className="text-[#00E676]" /></span>
          <h1 className="font-heading text-4xl uppercase tracking-tight text-white mb-3">Thank you</h1>
          <p className="text-[#9CA3AF]">Your {info?.recurring ? "monthly support" : "donation"}{info?.amount ? ` of $${info.amount}` : ""} keeps the archive alive. We appreciate you.</p>
          <Link to="/donate" data-testid="back-to-donate" className="inline-flex items-center gap-2 mt-7 px-5 py-3 rounded-xl bg-[#FF2A5F] hover:bg-[#e01e50] text-white font-semibold transition-colors"><Heart size={15} fill="white" /> Back</Link>
        </div>
      )}
      {state === "pending" && (
        <div data-testid="donate-pending">
          <Loader2 size={40} className="animate-spin text-[#FF2A5F] mx-auto mb-5" />
          <h1 className="font-heading text-2xl uppercase text-white mb-2">Almost there</h1>
          <p className="text-[#9CA3AF]">Your payment is still processing. If you were charged, your support will appear shortly — no need to pay again.</p>
          <Link to="/" className="inline-block mt-6 text-[#7fe9ff] hover:text-white">Back to home</Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid="donate-error">
          <span className="inline-flex w-16 h-16 rounded-full bg-[#E60023]/[0.15] items-center justify-center mb-5"><XCircle size={34} className="text-[#E60023]" /></span>
          <h1 className="font-heading text-2xl uppercase text-white mb-2">Payment not completed</h1>
          <p className="text-[#9CA3AF]">No charge was made. You can try again whenever you like.</p>
          <Link to="/donate" className="inline-block mt-6 px-5 py-3 rounded-xl bg-[#FF2A5F] hover:bg-[#e01e50] text-white font-semibold transition-colors">Try again</Link>
        </div>
      )}
    </div>
  );
}
